import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Paper, Stack, CircularProgress } from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { Dialog } from "../../../components/Dialogs/Dialog";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
//@ts-ignore
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { config } from "../../../config";
import { CountrySelector } from "../../../components/CountrySelector/CountrySelector";
import { COUNTRIES, ICountry } from "../../../constants/countries";

export interface IAddressDetails {
  place: string
  address: string;
  address_line1?: string;
  address_line2?: string;
  city: string;
  state: string;
  area: string;
  pin_code: string;
  country?: string;
  region?: string;
  latitude: number;
  longitude: number;
}

interface IMapPickerProps {
  open: boolean;
  onClose: () => void;
  onHandleLocationSelect: (
    place: any,
    addressDetails?: IAddressDetails,
    selectedLocation?: ISelectedLocation,
  ) => void;
  isFromChatModule?: boolean;
  /** Header style of the underlying Dialog (defaults to "default"). */
  variant?: "default" | "error";
  /** Initial ISO country code to scope/bias the location search (default "IN"). */
  defaultCountryIso?: string;
}

export interface ISelectedLocation {
  place: string
  latitude: number;
  longitude: number;
  name: string;
  address: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pin_code?: string;
}

const containerStyle = {
  width: "100%",
  height: "400px",
};

const libraries: ("places" | "geometry")[] = ["places"];

export const MapPicker = (props: IMapPickerProps) => {
  const {
    open,
    onClose,
    onHandleLocationSelect,
    isFromChatModule = false,
    variant = "default",
    defaultCountryIso = "IN",
  } = props;
  const [selectedLocation, setSelectedLocation] =
    useState<ISelectedLocation | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  // Country used to bias/restrict the Places search (defaults to India,
  // or the provided defaultCountryIso).
  const [searchCountryIso, setSearchCountryIso] = useState(defaultCountryIso);
  const [isLoading, setIsLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 18.5204, lng: 73.8567 });
  const [mapZoom, setMapZoom] = useState(12);
  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: config.integration.googleMapsApiKey,
    libraries,
  });

  // Recenter the map to the selected country when it changes (skip first run
  // so the initial center is preserved). Uses the Google Geocoder to resolve
  // the country's viewport/center, then fits the map to it.
  const didInitCountryRef = useRef(false);
  useEffect(() => {
    if (!isLoaded) return;
    if (!didInitCountryRef.current) {
      didInitCountryRef.current = true;
      return;
    }

    const country = COUNTRIES.find((c) => c.code === searchCountryIso);
    const countryName = country?.name || searchCountryIso;

    // Clear any previous selection/marker on country change.
    setSelectedLocation(null);
    setSelectedPlace(null);
    setMarker(null);

    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { address: countryName, componentRestrictions: { country: searchCountryIso } },
        (results, status) => {
          if (status === "OK" && results && results[0]) {
            const result = results[0];
            const loc = result.geometry.location;
            setMapCenter({ lat: loc.lat(), lng: loc.lng() });
            setMapZoom(5);
            // Fit to the country's bounds/viewport if available.
            const bounds =
              result.geometry.bounds || result.geometry.viewport;
            if (bounds && mapRef.current) {
              mapRef.current.fitBounds(bounds);
            }
          }
        },
      );
    } catch (e) {
      // Geocoder unavailable — leave the map as-is.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCountryIso, isLoaded]);

  // Helper function to parse address components
  const parseAddressComponents = (addressComponents: any[]) => {
    let city = "";
    let state = "";
    let country = "";
    let pin_code = "";
    let address_line1 = "";
    let address_line2 = "";

    for (const component of addressComponents) {
      const types = component.types;
      if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        state = component.long_name;
      } else if (types.includes("country")) {
        country = component.long_name;
      } else if (types.includes("postal_code")) {
        pin_code = component.long_name;
      } else if (
        types.includes("street_number") ||
        types.includes("route") ||
        types.includes("premise")
      ) {
        address_line1 += component.long_name + " ";
      } else if (
        types.includes("neighborhood") ||
        types.includes("sublocality") ||
        types.includes("sublocality_level_1") ||
        types.includes("sublocality_level_2")
      ) {
        address_line2 += component.long_name + " ";
      }
    }

    return {
      city,
      state,
      country,
      pin_code,
      address_line1: address_line1.trim(),
      address_line2: address_line2.trim(),
    };
  };

  const handlePlaceChange = async (place: any) => {
    if (!place || !place.value) {
      setSelectedLocation(null);
      setSelectedPlace(null);
      return;
    }

    const placeId = place.value.place_id;
    if (!placeId) return;

    setIsLoading(true);
    setSelectedPlace(place);

    try {
      const response = await geocodeByPlaceId(placeId);
      const { geometry, address_components, formatted_address } = response[0];
      const { lat, lng } = geometry.location;

      // Parse address components
      const parsedAddress = parseAddressComponents(address_components);

      // Try to get a meaningful name
      let name = "";
      for (const component of address_components) {
        const types = component.types;
        if (types.includes("establishment")) {
          name = component.long_name;
          break;
        } else if (types.includes("locality") && !name) {
          name = component.long_name;
        } else if (types.includes("route") && !name) {
          name = component.long_name;
        }
      }

      if (!name && formatted_address) {
        name = formatted_address.split(",")[0];
      }

      console.log("handleMapPickerLocationSelect MAP:Change ==> ", response, place)
      const locationData: ISelectedLocation = {
        latitude: lat(),
        longitude: lng(),
        name: name || "Selected Location",
        address: formatted_address,
        place: place?.value?.structured_formatting?.main_text ?? "",
        ...parsedAddress,
      };

      setSelectedLocation(locationData);
      setMarker({ lat: lat(), lng: lng() });
      setMapCenter({ lat: lat(), lng: lng() });
      setMapZoom(15);

      if (mapRef.current) {
        mapRef.current.setCenter({ lat: lat(), lng: lng() });
        mapRef.current.setZoom(15);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching location data:", error);
      setIsLoading(false);
      setSelectedLocation(null);
      setSelectedPlace(null);
    }
  };

  // Get place name by placeId using Places API getDetails
  const getPlaceNameById = (placeId: string): Promise<string> => {
    return new Promise((resolve) => {
      if (!mapRef.current || !placeId) {
        resolve("");
        return;
      }
      const placesService = new window.google.maps.places.PlacesService(mapRef.current);
      placesService.getDetails(
        {
          placeId,
          fields: ["name"],
        },
        (place, status) => {
          if (status === "OK" && place?.name) {
            resolve(place.name);
          } else {
            resolve("");
          }
        }
      );
    });
  };

  // Get the nearest establishment/place name using Places API
  const getNearbyPlaceName = (
    lat: number,
    lng: number
  ): Promise<string> => {
    return new Promise((resolve) => {
      if (!mapRef.current) {
        resolve("");
        return;
      }
      const placesService = new window.google.maps.places.PlacesService(mapRef.current);
      placesService.nearbySearch(
        {
          location: { lat, lng },
          radius: 50,
          type: "establishment",
        },
        (places, status) => {
          if (status === "OK" && places && places.length > 0) {
            // Prefer actual establishments/points of interest over localities
            const establishment = places.find((p) =>
              p.types?.some((t) =>
                ["establishment", "point_of_interest"].includes(t)
              )
            );
            resolve(establishment?.name || places[0].name || "");
          } else {
            resolve("");
          }
        }
      );
    });
  };

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const clickedPlaceId = (e as any).placeId as string | undefined;

    setMarker({ lat, lng });
    setIsLoading(true);

    console.log("getPlaceNameById ", clickedPlaceId)
    try {
      const geocoder = new window.google.maps.Geocoder();
      // Use placeId from click event if available (exact place), otherwise fall back to nearbySearch
      const placeName = clickedPlaceId
        ? await getPlaceNameById(clickedPlaceId)
        : await getNearbyPlaceName(lat, lng);
      geocoder.geocode({ location: { lat, lng } }, (results: google.maps.GeocoderResult[] | null, status) => {
        if (status === "OK" && results && results[0]) {
          const address = results[0].formatted_address;
          const addressComponents = results[0].address_components || [];

          // Parse address components
          const parsedAddress = parseAddressComponents(addressComponents);

          let name = "Selected Location";
          for (const component of addressComponents) {
            const types = component.types;
            if (types.includes("establishment")) {
              name = component.long_name;
              break;
            } else if (
              types.includes("locality") &&
              name === "Selected Location"
            ) {
              name = component.long_name;
            } else if (
              types.includes("route") &&
              name === "Selected Location"
            ) {
              name = component.long_name;
            }
          }

          if (name === "Selected Location" && address) {
            name = address.split(",")[0];
          }

          const locationData: ISelectedLocation = {
            latitude: lat,
            longitude: lng,
            address: address,
            name: placeName || name,
            place: placeName || name,
            ...parsedAddress,
          };

          setSelectedLocation(locationData);

          const placeFromMap = {
            value: {
              place_id: results[0].place_id,
              description: address,
            },
            label: placeName || name,
          };

          setSelectedPlace(placeFromMap);
        } else {
          const locationData: ISelectedLocation = {
            latitude: lat,
            longitude: lng,
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
            name: "Selected Location",
            place: ""
          };
          setSelectedLocation(locationData);
        }
        setIsLoading(false);
      },
      );
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setIsLoading(false);
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleSendLocation = () => {
    if (selectedLocation) {
      // Create address details object
      const addressDetails: IAddressDetails = {
        place: selectedLocation.place,
        address: selectedLocation.address,
        address_line1:
          selectedLocation.address_line1 ||
          selectedLocation.address.split(",")[0],
        address_line2: selectedLocation.address_line2 || "",
        city: selectedLocation.city || "",
        state: selectedLocation.state || "",
        area: selectedLocation.city || "",
        pin_code: selectedLocation.pin_code || "",
        country: selectedLocation.country || "",
        region: "IND",
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
      };

      // Pass both the place and structured address details
      onHandleLocationSelect(selectedPlace, addressDetails, selectedLocation);

      // Reset state
      setSelectedLocation(null);
      setSelectedPlace(null);
    }
  };

  const handleClose = () => {
    setSelectedLocation(null);
    setSelectedPlace(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      title="Select Location"
      subtitle="Search for a place or drop a pin to set the address."
      headerIcon={<LocationOn />}
      onClose={handleClose}
      size="lg"
      variant={variant}
      actions={[
        {
          type: "button",
          label: "Cancel",
          variant: "text",
          onClick: handleClose,
        },
        {
          type: "button",
          label: isFromChatModule ? "Send Location" : "Set Location",
          variant: "contained",
          color: "black",
          onClick: handleSendLocation,
          disabled: !selectedLocation,
        },
      ]}
    >
      <Box>
        {isLoaded ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
            <Box sx={{ width: 170, flexShrink: 0 }}>
              <CountrySelector
                value={searchCountryIso}
                fullWidth
                onChange={(country: ICountry) =>
                  setSearchCountryIso(country.code)
                }
                sx={{
                  "&.MuiOutlinedInput-root": {
                    minHeight: 44,
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <GooglePlacesAutocomplete
                apiKey={config.integration.googleMapsApiKey}
                // Re-instantiate when the country changes so the new
                // componentRestrictions take effect.
                key={searchCountryIso}
                selectProps={{
                  placeholder: "Search for a location...",
                  isClearable: true,
                  onChange: handlePlaceChange,
                  menuPortalTarget:
                    typeof document !== "undefined" ? document.body : undefined,
                  styles: {
                    control: (base: any) => ({
                      ...base,
                      minHeight: 44,
                      borderRadius: 8,
                    }),
                    menuPortal: (base: any) => ({ ...base, zIndex: 1400 }),
                  },
                }}
                autocompletionRequest={{
                  componentRestrictions: {
                    country: [searchCountryIso.toLowerCase()],
                  },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading...
            </Typography>
          </Box>
        )}

        {isLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2,
              p: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading location details...
            </Typography>
          </Box>
        )}

        {selectedLocation && !isLoading && (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              borderColor: "divider",
            }}
          >
            {/* Place name + address */}
            <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
              <LocationOn color="primary" sx={{ fontSize: 20, mt: 0.2 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                  {selectedLocation.place || selectedLocation.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.4}>
                  {selectedLocation.address}
                </Typography>
              </Box>
            </Box>

            {/* Details grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                gap: 1,
                pt: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              {selectedLocation.city && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    City
                  </Typography>
                  <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                    {selectedLocation.city}
                  </Typography>
                </Box>
              )}
              {selectedLocation.state && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    State
                  </Typography>
                  <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                    {selectedLocation.state}
                  </Typography>
                </Box>
              )}
              {selectedLocation.country && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    Country
                  </Typography>
                  <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                    {selectedLocation.country}
                  </Typography>
                </Box>
              )}
              {selectedLocation.pin_code && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    Pin Code
                  </Typography>
                  <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                    {selectedLocation.pin_code}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                  Lat, Lng
                </Typography>
                <Typography variant="body2" fontWeight={500} fontSize="0.8rem">
                  {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {!selectedLocation && !isLoading && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontSize: "0.75rem" }}
          >
            Search or click on the map to select a location
          </Typography>
        )}

        <Box
          sx={{
            mt: 3,
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {!isLoaded ? (
            <Box
              sx={{
                textAlign: "center",
                p: 3,
                height: containerStyle.height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading map...
              </Typography>
            </Box>
          ) : (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={mapZoom}
              onClick={handleMapClick}
              onLoad={onMapLoad}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
              }}
            >
              {marker && (
                <Marker
                  position={marker}
                  draggable
                  onDragEnd={handleMapClick}
                />
              )}
            </GoogleMap>
          )}

          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                bgcolor: "background.paper",
                p: 1,
                borderRadius: 1,
                boxShadow: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="caption">Loading...</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
